import { WorkPage } from '../components/work-page.tsx';
import './schedule-pages.css';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { planningApi, newPlanningId } from '../api/availability.ts';
import { PlanningConflictActions } from '../components/schedule/planning-conflict-actions.tsx';
import { SchedulingProfileCard } from '../components/schedule/scheduling-profile-card.tsx';
import { DispatchWeightsCard, ReservedSlotConflicts } from '../components/schedule/dispatch-sections.tsx';
import { companyApi, savedTeamsApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { PlanRequiredPage, usePlanFeature } from '../components/plan-gate.tsx';
import { Alert, Button, Field, LoadingBlock } from '../components/ui.tsx';
import { addDaysToDate, localDateTimeToUtc } from '../../shared/calculations/recurrence.ts';
import { todayInTimezone, zonedDateTime } from '../components/schedule/format.ts';
import type { AvailabilityWindow } from '../../shared/types-availability.ts';
import { formatLocalTime, formatNumericDate } from '../i18n/datetime.ts';
export default function PlanningAvailabilityPage() {
  const { t } = useTranslation();
  const enabled = usePlanFeature('scheduling');
  if (!enabled) return <PlanRequiredPage feature="scheduling" title={t('planning.title')} />;
  return <PlanningContent />;
}
function PlanningContent() {
  const { t } = useTranslation();
  const { company, currentLocation, can } = useSession();
  const queries = useQueryClient();
  const zone = currentLocation?.timezone ?? company?.timezone ?? 'UTC';
  const today = todayInTimezone(zone);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(addDaysToDate(today, 276));
  const [person, setPerson] = useState('');
  const isTeam = person.startsWith('team:');
  const subjectId = isTeam ? person.slice(5) : person;
  const subject = isTeam ? { teamId: subjectId } : { userId: subjectId };
  const [blockKind, setBlockKind] = useState<'available' | 'unavailable'>('unavailable');
  const [page, setPage] = useState(1);
  const [blockPage, setBlockPage] = useState(1);
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const cursor = cursors[page - 1];
  useEffect(() => {
    setPage(1);
    setBlockPage(1);
    setCursors([undefined]);
    setRequestId(newPlanningId());
  }, [from, to, person, company?.id, currentLocation?.id]);
  const [windows, setWindows] = useState<AvailabilityWindow[]>([]);
  const [revision, setRevision] = useState(0);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [blockDate, setBlockDate] = useState(today);
  const [blockToDate, setBlockToDate] = useState(today);
  const [blockStart, setBlockStart] = useState('09:00');
  const [blockEnd, setBlockEnd] = useState('17:00');
  const [requestId, setRequestId] = useState(newPlanningId);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  const scope = [company?.id, currentLocation?.id];
  const team = useQuery({ queryKey: ['planning-roster', ...scope], queryFn: () => companyApi.users() });
  const savedTeams = useQuery({
    queryKey: ['saved-teams', ...scope],
    queryFn: () => savedTeamsApi.list(),
    enabled: can('schedule:read'),
  });
  const conflicts = useQuery({
    queryKey: ['planning-conflicts', ...scope, from, to, person, page, cursor],
    queryFn: () => planningApi.conflicts({ from, to, ...(person ? subject : {}), page, pageSize: 25, cursor }),
    enabled: can('schedule:read'),
    retry: false,
  });
  const profile = useQuery({
    queryKey: ['planning-profile', ...scope, person],
    queryFn: () => (isTeam ? planningApi.teamProfile(subjectId) : planningApi.profile(subjectId)),
    enabled: Boolean(person) && can('schedule:read'),
    retry: false,
  });
  const blocks = useQuery({
    queryKey: ['planning-blocks', ...scope, person, from, to, blockPage],
    queryFn: () => planningApi.exceptions({ from, to, ...subject, page: blockPage }),
    enabled: Boolean(person) && can('schedule:read'),
    retry: false,
  });
  useEffect(() => {
    setMessage('');
    if (profile.data) {
      setWindows(profile.data.windows);
      setRevision(profile.data.revision);
    }
  }, [profile.data, person, currentLocation?.id]);
  const weekdays = [
    'schedule.page.weekdaySun',
    'schedule.page.weekdayMon',
    'schedule.page.weekdayTue',
    'schedule.page.weekdayWed',
    'schedule.page.weekdayThu',
    'schedule.page.weekdayFri',
    'schedule.page.weekdaySat',
  ];
  async function mutate(work: () => Promise<unknown>) {
    setBusy(true);
    setMessage('');
    try {
      await work();
      await queries.invalidateQueries({
        predicate: (q) =>
          String(q.queryKey[0]).startsWith('planning-') ||
          q.queryKey[0] === 'availability' ||
          q.queryKey[0] === 'dispatch-reservations',
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('planning.failed'));
    } finally {
      setBusy(false);
    }
  }
  const mayWrite = can('schedule:write') && online && !busy && Boolean(currentLocation);
  return (
    <WorkPage
      title={t('planning.title')}
      description={t('planning.description')}
      actions={
        <Link className="btn btn-secondary" to="/schedule">
          {t('planning.back')}
        </Link>
      }
    >
      {!online && (
        <Alert tone="warning" title={t('planning.offline')}>
          {t('planning.offlineBody')}
        </Alert>
      )}
      {message && (
        <Alert tone="error" title={t('planning.failed')}>
          {message}
        </Alert>
      )}
      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <Field label={t('planning.from')}>
          {({ id, describedBy }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              type="date"
              className="field-control min-w-0 w-full"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
            />
          )}
        </Field>
        <Field label={t('planning.to')}>
          {({ id, describedBy }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              type="date"
              className="field-control min-w-0 w-full"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
            />
          )}
        </Field>
        <Field label={t('planning.subject')}>
          {({ id, describedBy }) => (
            <select
              id={id}
              aria-describedby={describedBy}
              className="field-control min-w-0 w-full"
              value={person}
              onChange={(e) => {
                setPerson(e.target.value);
                setPage(1);
              }}
            >
              <option value="">{t('planning.everyone')}</option>
              {(savedTeams.data?.items ?? [])
                .filter((team) => team.isActive)
                .map((team) => (
                  <option key={team.id} value={`team:${team.id}`}>
                    {t('planning.teamName', { name: team.name })}
                  </option>
                ))}
              {(team.data?.items ?? [])
                .filter((p) => p.isActive)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.fullName}
                  </option>
                ))}
            </select>
          )}
        </Field>
      </div>
      {can('schedule:read') && (
        <ReservedSlotConflicts
          from={today}
          to={addDaysToDate(today, 90)}
          {...(person && !isTeam ? { userId: subjectId } : {})}
        />
      )}
      <section className="card mb-5 p-4" aria-label={t('planning.conflicts')}>
        <h2 className="mb-2 text-lg font-semibold">{t('planning.conflicts')}</h2>
        <p className="mb-3 text-sm">{t('planning.materialized')}</p>
        {conflicts.isPending && <LoadingBlock />}
        {conflicts.isError && (
          <Alert tone="error" title={t('planning.failed')}>
            <Button
              onClick={() => {
                setPage(1);
                setCursors([undefined]);
                void queries.invalidateQueries({ queryKey: ['planning-conflicts'] });
              }}
            >
              {t('planning.retry')}
            </Button>
          </Alert>
        )}
        {conflicts.data?.truncated && (
          <Alert tone="warning" title={t('planning.partial')}>
            {t('planning.narrow')}
          </Alert>
        )}
        {conflicts.data?.items.length === 0 && conflicts.data.scanComplete && <p>{t('planning.none')}</p>}
        <ul className="space-y-3">
          {conflicts.data?.items.map((row, i) => (
            <li key={`${row.appointmentId}-${row.reason}-${i}`} className="rounded border p-3">
              <p className="font-semibold">{row.customerName}</p>
              {row.teamId && (
                <p>
                  {t('planning.teamName', {
                    name:
                      savedTeams.data?.items.find((team) => team.id === row.teamId)?.name ?? t('availability.someone'),
                  })}
                </p>
              )}
              <p>
                {formatNumericDate(zonedDateTime(row.startsAt, zone).date)} ·{' '}
                {formatLocalTime(zonedDateTime(row.startsAt, zone).time)} · {t(`planning.reason.${row.reason}`)}
              </p>
              <p>
                {row.userIds
                  .map((id) => team.data?.items.find((p) => p.id === id)?.fullName ?? t('availability.someone'))
                  .join(', ')}
              </p>
              {can('schedule:write') && <PlanningConflictActions row={row} enabled={mayWrite} />}
            </li>
          ))}
        </ul>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>
            {t('planning.previous')}
          </Button>
          <span>{page}</span>
          <Button
            disabled={!conflicts.data?.nextCursor || conflicts.isFetching}
            onClick={() => {
              if (conflicts.data?.nextCursor) {
                setCursors((previous) => [...previous.slice(0, page), conflicts.data.nextCursor!]);
                setPage(page + 1);
              }
            }}
          >
            {t('planning.next')}
          </Button>
        </div>
      </section>
      {person && (
        <section className="card p-4" aria-label={t('planning.weekly')}>
          <h2 className="text-lg font-semibold">{t('planning.weekly')}</h2>
          <p className="mb-3 text-sm">{t('planning.intersection')}</p>
          {profile.isPending && <LoadingBlock />}
          {profile.isError && <Button onClick={() => void profile.refetch()}>{t('planning.retry')}</Button>}
          {profile.data && (
            <>
              <p>
                {isTeam
                  ? t(profile.data.inherited ? 'planning.teamIntersection' : 'planning.teamOverride')
                  : t(profile.data.inherited ? 'planning.inherited' : 'planning.custom')}
              </p>
              {windows.map((w, index) => (
                <div key={index} className="my-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <select
                    aria-label={t('planning.weekday')}
                    className="field-control min-w-0 col-span-2 sm:col-span-1"
                    value={w.weekday}
                    disabled={!mayWrite}
                    onChange={(e) =>
                      setWindows(windows.map((v, i) => (i === index ? { ...v, weekday: Number(e.target.value) } : v)))
                    }
                  >
                    {weekdays.map((key, i) => (
                      <option key={key} value={i}>
                        {t(key)}
                      </option>
                    ))}
                  </select>
                  <input
                    aria-label={t('planning.start')}
                    type="time"
                    className="field-control min-w-0"
                    disabled={!mayWrite}
                    value={w.startTime}
                    onChange={(e) =>
                      setWindows(windows.map((v, i) => (i === index ? { ...v, startTime: e.target.value } : v)))
                    }
                  />
                  <input
                    aria-label={t('planning.end')}
                    type="time"
                    className="field-control min-w-0"
                    disabled={!mayWrite}
                    value={w.endTime}
                    onChange={(e) =>
                      setWindows(windows.map((v, i) => (i === index ? { ...v, endTime: e.target.value } : v)))
                    }
                  />
                  <Button disabled={!mayWrite} onClick={() => setWindows(windows.filter((_, i) => i !== index))}>
                    {t('planning.remove')}
                  </Button>
                </div>
              ))}
              <div className="my-3 flex flex-wrap gap-2">
                <Button
                  disabled={!mayWrite || windows.length >= 28}
                  onClick={() => setWindows([...windows, { weekday: 1, startTime: '09:00', endTime: '17:00' }])}
                >
                  {t('planning.addWindow')}
                </Button>
                <Button
                  disabled={!mayWrite}
                  onClick={() =>
                    void mutate(() =>
                      isTeam
                        ? planningApi.saveTeamProfile(subjectId, { revision, windows })
                        : planningApi.saveProfile(subjectId, { revision, windows }),
                    )
                  }
                >
                  {t('planning.save')}
                </Button>
                <Button
                  disabled={!mayWrite}
                  onClick={() =>
                    void mutate(() =>
                      isTeam
                        ? planningApi.saveTeamProfile(subjectId, { revision, windows: [], inherit: true })
                        : planningApi.saveProfile(subjectId, { revision, windows: [], inherit: true }),
                    )
                  }
                >
                  {t(isTeam ? 'planning.useIntersection' : 'planning.useInherited')}
                </Button>
              </div>
              <p className="text-sm">{t('planning.emptyWeek')}</p>
            </>
          )}
          {!isTeam && <SchedulingProfileCard key={subjectId} userId={subjectId} mayWrite={mayWrite} />}
          <h3 className="mt-5 font-semibold">{t('planning.blocks')}</h3>
          <label className="field-label">
            {t('planning.exceptionKind')}
            <select
              className="field-control min-w-0 w-full"
              value={blockKind}
              disabled={!mayWrite}
              onChange={(event) => {
                setBlockKind(event.target.value as 'available' | 'unavailable');
                setRequestId(newPlanningId());
              }}
            >
              <option value="unavailable">{t('planning.unavailable')}</option>
              <option value="available">{t('planning.available')}</option>
            </select>
          </label>
          <p className="field-hint">{t('planning.availableHint')}</p>
          <div className="my-3 grid gap-2 sm:grid-cols-3">
            <Field label={t('planning.date')}>
              {({ id, describedBy }) => (
                <input
                  id={id}
                  aria-describedby={describedBy}
                  type="date"
                  className="field-control min-w-0 w-full"
                  value={blockDate}
                  onChange={(e) => {
                    setBlockDate(e.target.value);
                    setBlockToDate(e.target.value);
                    setRequestId(newPlanningId());
                  }}
                />
              )}
            </Field>
            <Field label={t('planning.to')}>
              {({ id, describedBy }) => (
                <input
                  id={id}
                  aria-describedby={describedBy}
                  type="date"
                  className="field-control min-w-0 w-full"
                  value={blockToDate}
                  onChange={(e) => {
                    setBlockToDate(e.target.value);
                    setRequestId(newPlanningId());
                  }}
                />
              )}
            </Field>
            <Field label={t('planning.start')}>
              {({ id, describedBy }) => (
                <input
                  id={id}
                  aria-describedby={describedBy}
                  type="time"
                  className="field-control min-w-0 w-full"
                  value={blockStart}
                  onChange={(e) => {
                    setBlockStart(e.target.value);
                    setRequestId(newPlanningId());
                  }}
                />
              )}
            </Field>
            <Field label={t('planning.end')}>
              {({ id, describedBy }) => (
                <input
                  id={id}
                  aria-describedby={describedBy}
                  type="time"
                  className="field-control min-w-0 w-full"
                  value={blockEnd}
                  onChange={(e) => {
                    setBlockEnd(e.target.value);
                    setRequestId(newPlanningId());
                  }}
                />
              )}
            </Field>
          </div>
          <Button
            disabled={!mayWrite || blockToDate < blockDate || (blockToDate === blockDate && blockStart === blockEnd)}
            onClick={() =>
              void mutate(async () => {
                await planningApi.addBlock({
                  id: requestId,
                  ...subject,
                  kind: blockKind,
                  startsAt: localDateTimeToUtc(blockDate, blockStart, zone),
                  endsAt: localDateTimeToUtc(
                    blockToDate === blockDate && blockEnd < blockStart ? addDaysToDate(blockDate, 1) : blockToDate,
                    blockEnd,
                    zone,
                  ),
                });
                setRequestId(newPlanningId());
              })
            }
          >
            {t(blockKind === 'available' ? 'planning.addAvailable' : 'planning.addBlock')}
          </Button>
          <ul className="mt-3 space-y-2">
            {blocks.data?.items.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  {formatNumericDate(zonedDateTime(b.startsAt, zone).date)} ·{' '}
                  {formatLocalTime(zonedDateTime(b.startsAt, zone).time)} /{' '}
                  {formatLocalTime(zonedDateTime(b.endsAt, zone).time)} ·{' '}
                  {t(
                    b.source === 'time_off'
                      ? 'planning.timeOff'
                      : b.kind === 'available'
                        ? 'planning.available'
                        : 'planning.unavailable',
                  )}
                </span>
                {b.source === 'manual' && (
                  <Button disabled={!mayWrite} onClick={() => void mutate(() => planningApi.removeBlock(b))}>
                    {t('planning.remove')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
          <p>
            {t('planning.exceptionsPage', {
              page: blockPage,
              pages: Math.max(1, blocks.data?.totalPages ?? 1),
              total: blocks.data?.total ?? blocks.data?.items.length ?? 0,
            })}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button disabled={blockPage <= 1 || blocks.isFetching} onClick={() => setBlockPage(blockPage - 1)}>
              {t('planning.previousExceptions')}
            </Button>
            <Button
              disabled={blockPage >= (blocks.data?.totalPages ?? 1) || blocks.isFetching}
              onClick={() => setBlockPage(blockPage + 1)}
            >
              {t('planning.nextExceptions')}
            </Button>
          </div>
        </section>
      )}
      {can('schedule:read') && <DispatchWeightsCard canEdit={can('company:write')} />}
    </WorkPage>
  );
}
